import { initFirebase } from '../firebase'
import { collection, getDocs, query, orderBy, limit, where, doc, getDoc, onSnapshot } from 'firebase/firestore'

const { db } = initFirebase()

// Compatibility Report cost & tracking — the third paid Store report
// (reportType == 'compatibility'). Synchronous like Year Ahead: no task queue,
// no PDF. Staged generation with five slices (meters, spine, overview, between,
// snapshot) — the score meters land first; spine runs on claude-fable-5 and the
// rest on claude-sonnet-5, so cost and model are read per slice, never assumed.
//   reportStats/compatibility      — write-time rollup (one read): the headline
//     avg/total cost + duration numbers.
//   report_generations (reportType == 'compatibility') — one row per run with a
//     per-slice cost breakdown.
//   reports (reportType == 'compatibility') — the report itself: scores, people,
//     generation state + per-section progress. Used for the "recent reports" view.
//   system_logs (functionName == 'generateCompatibilityReport') — error/warn logs.
//
// Same access path as /year-ahead and /natal-chart (ryomen rules allow authed
// reads; the app signs in anonymously). The collection reads use a single-field
// where('reportType','==','compatibility') — auto-indexed, no composite index —
// then sort/limit client-side, so no compatibility row is ever missed.

// Five slices, in the order the backend declares generation.sections. 'meters'
// (the score cards) is written first; 'spine' is internal (seeds the one-sentence
// reading). Both are billed slices, so they show in the cost breakdown.
export const COMPATIBILITY_SLICES = ['meters', 'spine', 'overview', 'between', 'snapshot']

const ERROR_FN = 'generateCompatibilityReport'

function num(v) {
  if (typeof v === 'number') return v
  if (v == null) return 0
  const n = Number(v)
  return isNaN(n) ? 0 : n
}

function toMs(v) {
  if (!v) return 0
  if (typeof v === 'object' && typeof v.toMillis === 'function') return v.toMillis()
  if (typeof v === 'object' && (v.seconds || v._seconds)) return (v.seconds || v._seconds) * 1000
  const t = new Date(v).getTime()
  return isNaN(t) ? 0 : t
}

function normStats(x) {
  return {
    count: num(x.count),
    completed: num(x.completed),
    partial: num(x.partial),
    failed: num(x.failed),
    repairs: num(x.repairs),
    totalCostUSD: num(x.totalCostUSD),
    totalMs: num(x.totalMs),
    totalInputTokens: num(x.totalInputTokens),
    totalOutputTokens: num(x.totalOutputTokens),
    lastAtMs: toMs(x.lastAt)
  }
}

function mapReport(d) {
  const x = d.data()
  const g = x.generation || {}
  const readyAt = (g.sectionsReadyAt && typeof g.sectionsReadyAt === 'object') ? g.sectionsReadyAt : {}
  const sectionsReadyAtMs = {}
  Object.keys(readyAt).forEach(k => { sectionsReadyAtMs[k] = toMs(readyAt[k]) })
  const people = x.people || {}
  const partner = people.partner || {}
  const scores = x.scores || {}
  return {
    id: d.id,
    reportId: x.reportId || d.id,
    userId: x.userId || '',
    language: x.language || '',
    title: x.title || '',
    user: people.user || {},
    partner,
    partnerKind: partner.kind || '',
    relationship: partner.relationship || '',
    scoreOverall: num(scores.overall),
    scoreTier: scores.tier || '',
    scoreItems: Array.isArray(scores.items) ? scores.items.map(i => ({ id: i.id || '', score: num(i.score), filled: num(i.filled) })) : [],
    purchase: x.purchase || null,
    state: g.state || '',
    sections: (g.sections && typeof g.sections === 'object') ? g.sections : {},
    sectionsReadyAtMs,
    promptVersion: g.promptVersion || '',
    createdAtMs: toMs(x.createdAt) || toMs(x.purchasedAt),
    purchasedAtMs: toMs(x.purchasedAt),
    startedAtMs: toMs(g.startedAt),
    finishedAtMs: toMs(g.finishedAt)
  }
}

function mapGen(d) {
  const x = d.data()
  return {
    id: d.id,
    reportId: x.reportId || '',
    userId: x.userId || '',
    language: x.language || '',
    promptVersion: x.promptVersion || '',
    state: x.state || '',
    partialRepair: x.partialRepair === true,
    partnerKind: x.partnerKind || '',
    totalMs: num(x.totalMs),
    inputTokens: num(x.inputTokens),
    outputTokens: num(x.outputTokens),
    costUsd: num(x.costUsd),
    slices: (x.slices && typeof x.slices === 'object') ? x.slices : {},
    slicesFailed: Array.isArray(x.slicesFailed) ? x.slicesFailed : [],
    createdAtMs: toMs(x.createdAt) || toMs(x.timestamp)
  }
}

export class FirebaseCompatibilityReportService {
  // Write-time rollup — the headline numbers, no scan.
  async getStats() {
    const snap = await getDoc(doc(db, 'reportStats', 'compatibility'))
    return snap.exists() ? normStats(snap.data()) : null
  }

  // Per-generation rows, newest first. Single-field where (auto-indexed);
  // sorted client-side so no compatibility row is missed.
  async getGenerations(max = 500) {
    const snap = await getDocs(query(collection(db, 'report_generations'), where('reportType', '==', 'compatibility')))
    return snap.docs
      .map(mapGen)
      .sort((a, b) => b.createdAtMs - a.createdAtMs)
      .slice(0, max)
  }

  // The reports themselves (scores, people, per-section state), newest first.
  async getReports(max = 200) {
    const snap = await getDocs(query(collection(db, 'reports'), where('reportType', '==', 'compatibility')))
    return snap.docs
      .map(mapReport)
      .sort((a, b) => b.createdAtMs - a.createdAtMs)
      .slice(0, max)
  }

  // Real-time watch of all compatibility reports (single-field where, no index),
  // so in-flight runs and their per-section progress update live while watched.
  // Returns the unsubscribe fn. Sorted newest first.
  watchReports(onData, onError) {
    const q = query(collection(db, 'reports'), where('reportType', '==', 'compatibility'))
    return onSnapshot(
      q,
      snap => onData(snap.docs.map(mapReport).sort((a, b) => b.createdAtMs - a.createdAtMs)),
      err => { if (onError) onError(err) }
    )
  }

  async getErrorLogs(max = 300) {
    const snap = await getDocs(query(collection(db, 'system_logs'), orderBy('createdAt', 'desc'), limit(max)))
    const cutoff = Date.now() - 7 * 86400000
    return snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(x => x.functionName === ERROR_FN && (x.level === 'error' || x.level === 'warn') && toMs(x.createdAt) >= cutoff)
      .map(x => ({
        id: x.id,
        level: x.level,
        message: x.message || '',
        error: x.error || '',
        reportId: (x.metadata && x.metadata.reportId) || '',
        failedSlices: (x.metadata && x.metadata.failedSlices) || null,
        createdAtMs: toMs(x.createdAt)
      }))
      .sort((a, b) => b.createdAtMs - a.createdAtMs)
  }
}
